import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import formatEstimateMs from 'util/formatEstimateMs';
import {capitalize} from 'util/NameUtil';

import {TimingEstimateResult} from './useTimingEstimate';

export interface TimingEstimateBadgeProps {
  estimate: TimingEstimateResult;
  action: 'update' | 'creation';
  objectName: string;
}

/**
 * Informational inline that shows the estimated time for an action to complete.
 *
 * If the action is expected to timeout, it renders as a warning badge.
 */
export default function TimingEstimateBadge(props: TimingEstimateBadgeProps): JSX.Element | null {
  const {stale, executionPlan} = props.estimate;
  const isNearTimeout = executionPlan != null && executionPlan.isNearTimeout;
  const expectTimeout = executionPlan != null && executionPlan.expectTimeout;
  const is = executionPlan && stale ? 'was' : 'is';
  if (executionPlan == null && !stale) {
    // If caller decided that the input was invalid or the action was already completed
    // (e.g. ProcessDevelopmentStudyDesignTabContents), and thus needed no estimate, then show nothing.
    return null;
  }
  return (
    <Tooltip
      text={
        <>
          <p className={css(styles.p)}>
            <strong>Estimated {props.action} time</strong> {is}{' '}
            {executionPlan ? <>{formatEstimateMs(executionPlan.estimatedTime)}.</> : '...'}
            {executionPlan && stale && ' Recalculating...'}
          </p>
          {(isNearTimeout || expectTimeout) && (
            <p className={css(styles.p, styles.nextP)}>
              <em>
                {capitalize(props.action)} of this {props.objectName}{' '}
                {expectTimeout ? `${is} expected to` : 'may'} exceed the maximum allowed time and fail.
              </em>
            </p>
          )}
        </>
      }
    >
      <Badge stale={stale} warning={isNearTimeout || expectTimeout}>
        <BenchlingIcon icon="clock" />{' '}
        {executionPlan ? formatEstimateMs(executionPlan.estimatedTime, {short: true}) : '...'}
      </Badge>
    </Tooltip>
  );
}

function Badge({
  children,
  stale,
  warning,
}: {
  children: React.ReactNode;
  stale?: boolean;
  warning?: boolean;
}): JSX.Element {
  return (
    <span
      className={css(
        styles.estimateBadge,
        stale && styles.estimateBadgeStale,
        warning && styles.estimateBadgeWarning
      )}
    >
      {children}
    </span>
  );
}

const styles = StyleSheet.create({
  p: {margin: 0},
  nextP: {marginTop: 5},
  estimateBadge: {
    margin: '-4px -6px',
    padding: '4px 6px',
    borderRadius: 2,
    fontWeight: 400,
  },
  estimateBadgeStale: {opacity: 0.5},
  estimateBadgeWarning: {
    backgroundColor: Colors.ORANGE_LIGHTER,
    color: Colors.ORANGE_DARK,
  },
});
